import { getHistory } from 'lib/history'
import { storeComingSoonPath } from 'lib/urls'
import {
  pick,
  omitBy,
  mapValues,
  values,
  min,
  max,
  uniq,
  castArray,
  isEmpty,
  isArray,
} from 'lodash'
import { selectProducts } from 'store/api/hooks'

export const SECTIONS = {
  SORT: 'Sort',
  BRANDS: 'Brand',
  RATING: 'Rating',
  ON_SALE: 'On Sale',
  IN_STOCK: 'In Stock',
  PRICE_RANGE: 'Price Range',
  PRODUCT_TYPE: 'Product Type',
}

export const sectionOptions = values(SECTIONS)

export const FILTERS = {
  BRANDS: 'brands',
  ON_SALE: 'onSale',
  CATEGORIES: 'categories',
  MIN_PRICE: 'minPrice',
  MAX_PRICE: 'maxPrice',
  AVAILABLE: 'available',
  MIN_RATING: 'minRating',
}

const filterOptions = values(FILTERS)

export const isEmptyFilter = (filter) =>
  Array.isArray(filter) && filter.length === 0

export const filterEmptyFilters = (filters) => {
  return Object.entries(filters).reduce(
    (accumulatedFilters, [filterName, filterValue]) => {
      if (!isEmptyFilter(filterValue)) {
        accumulatedFilters[filterName] = filterValue
      }
      return accumulatedFilters
    },
    {}
  )
}

const omitValues = (val) => {
  if (isArray(val)) {
    return isEmpty(val)
  }
  return !val
}

export const clearedFilterOptions = {
  [FILTERS.BRANDS]: undefined,
  [FILTERS.ON_SALE]: undefined,
  [FILTERS.CATEGORIES]: undefined,
  [FILTERS.MIN_PRICE]: undefined,
  [FILTERS.MAX_PRICE]: undefined,
  [FILTERS.AVAILABLE]: undefined,
  [FILTERS.MIN_RATING]: undefined,
}

export function encodeFilterForQueryString(name, value) {
  if (!name) return null

  if (isArray(value)) {
    return value.map((v) => encodeFilterForQueryString(name, v)).join('&')
  }

  return `${name}=${encodeURIComponent(value || '')}`
}

export function uriEncodeFilters(filters) {
  if (!filters) return ''

  const appendQueryParam = (query, param) =>
    [query, encodeFilterForQueryString(param, filters[param])].join('&')

  // reduce next query hash into a string joined by '&' and replace the first
  // '&' with a '?' (since the first join() will join the first param with the empty string)
  return Object.keys(filters).reduce(appendQueryParam, '').replace('&', '?')
}

export const updateUrlFilterParams = (filters) => {
  const history = getHistory({ useRouter: true })
  const location = history.getCurrentLocation()
  const filterQuery = pick(filters, filterOptions)
  // omit newly disabled filters like available=false
  const nextQuery = omitBy({ ...location.query, ...filterQuery }, omitValues)
  history.replace({
    search: uriEncodeFilters(nextQuery),
    state: location.state,
    pathname: location.pathname,
  })
}

const coerceFilterTypes = (value, key) => {
  if (!value) return undefined
  switch (key) {
    case FILTERS.ON_SALE:
    case FILTERS.AVAILABLE:
      return Boolean(value)
    case FILTERS.MIN_PRICE:
    case FILTERS.MAX_PRICE:
    case FILTERS.MIN_RATING:
      return Number(value)
    case FILTERS.BRANDS:
    case FILTERS.CATEGORIES:
      return castArray(value)
    default:
      return value
  }
}

export const getInitialFilterParams = () => {
  const location = getHistory({ useRouter: true }).getCurrentLocation()
  const urlFilters = pick(location.query, filterOptions)
  return mapValues(urlFilters, coerceFilterTypes)
}

export const getFilteredProducts = (products, filters) => {
  return selectProducts(products, { filters })
}

export const getFilteredProductsCount = (products, filters) => {
  return getFilteredProducts(products, filters)?.length
}

export const getPriceRange = (products, currentMin, currentMax) => {
  const minPrice = min([
    currentMin,
    ...products.map((prod) => prod.pricing?.min),
  ])
  const maxPrice = max([
    currentMax,
    ...products.map((prod) => prod.pricing?.max),
  ])
  return [minPrice, maxPrice]
}
const compareAscending = (a, b) => (a < b ? -1 : a > b ? 1 : 0)
const compareCaseInsensitiveAscending = (a, b) =>
  compareAscending(a.toUpperCase(), b.toUpperCase())

export const getBrandOptions = (products) =>
  uniq(products?.map((prod) => prod.brand))
    .filter((prod) => !!prod)
    .sort(compareCaseInsensitiveAscending)

export const getCategoryOptions = (products) =>
  uniq(products?.map((prod) => prod.category))
    .filter((prod) => !!prod)
    .sort()

export const areFiltersApplied = (filters) => {
  const appliedOnly = omitBy(filters, omitValues)
  return values(appliedOnly).length > 0
}

export const hideProductType = () => {
  const location = getHistory({ useRouter: true }).getCurrentLocation()
  return location.pathname === storeComingSoonPath
}
